<template>
	<div
		class="amelipro-radio-group"
		:class="{
			'pills': pills,
			'classic': !pills,
		}"
	>
		<div
			class="d-flex flex-column radio-group"
			:class="{
				'flex-md-row': horizontalLabel || fullHorizontal,
				'align-md-center': !horizontalLabel && fullHorizontal,
				'align-md-start': horizontalLabel && !fullHorizontal,
			}"
		>
			<div class="d-flex align-center mr-2 radio-group__label-wrapper">
				<p
					:id="groupId"
					class="radio-group__label d-inline-flex"
					:class="{
						'mb-1': !fullHorizontal && !pills,
						'mb-0 mb-md-4': fullHorizontal || pills,
						'margin-label': horizontalLabel && !fullHorizontal,
					}"
				>
					<slot name="groupLabel">
						{{ groupLabel }}
					</slot>

					<span
						v-if="ariaRequired"
						aria-hidden="true"
						class="d-inline-flex"
					>
						&nbsp;*
					</span>
					<span
						v-if="ariaRequired"
						class="d-sr-only"
					>
						&nbsp;Champ obligatoire
					</span>
				</p>

				<slot name="labelInfo" />
			</div>

			<div
				:aria-labelledby="groupId"
				:aria-required="ariaRequired"
				class="radio-group__wrapper"
				:class="{
					'mt-1 mt-md-0': fullHorizontal && !pills,
					'mt-2': pills
				}"
				role="radiogroup"
			>
				<ul
					class="d-flex flex-wrap list-style-none radio-group__list"
					:class="{
						'flex-column': !pills,
						'flex-md-row align-md-center': !horizontalLabel && (horizontal || fullHorizontal),
					}"
				>
					<li
						v-for="(item, index) in items"
						:key="index"
						class="radio-group__item"
						:class="{
							'mb-2': pills,
							'mb-4': !pills,
						}"
					>
						<div class="d-flex align-center mr-2 amelipro-radio-group__label-wrapper">
							<label
								class="d-inline-flex flex-row align-center mr-0 text-body-1 radio-group__item__label"
								:class="{
									'mr-md-2': !pills && (horizontal || fullHorizontal),
								}"
							>
								<input
									:id="`${groupId}-input-${index}`"
									v-model="selectedValue"
									:aria-controls="slotSubItem(index) ? `${groupId}-subitem-${index}` : undefined"
									:aria-describedby="checkErrorCondition ? `${groupId}-error` : undefined"
									:aria-expanded="ariaExpandedValue(item, index)"
									:disabled="disabled"
									:name="`${groupId}-name`"
									:required="ariaRequired"
									type="radio"
									:value="item.value"
									@change.stop.prevent="emitChangeEvent(item)"
								>

								<span
									:class="{
										'ap-grey--text': !pills && disabled,
										'ap-blue--text text--darken-1': pills && !checkErrorCondition && !disabled,
										'ap-error--text radio-group__item__label__span__error': checkErrorCondition && !disabled,
										'ap-grey--text text--darken-1': (!pills && !checkErrorCondition && !disabled) || (pills && disabled),
									}"
								>
									{{ item.label }}
								</span>
							</label>

							<slot :name="`append-${index}`" />
						</div>

						<div
							v-if="slotSubItem(index) && isChecked(item.value)"
							:id="`${groupId}-subitem-${index}`"
						>
							<slot :name="`subItem-${index}`" />
						</div>
					</li>
				</ul>
			</div>
		</div>

		<AmeliproMessage
			v-if="checkErrorCondition"
			:id="`${groupId}-error`"
			no-icon
			text
			type="error"
		>
			<p class="mb-0">
				{{ requiredErrorMessage }}
			</p>
		</AmeliproMessage>
	</div>
</template>

<script setup lang="ts">
	import { PropType, computed, ref, useSlots } from 'vue';
	import AmeliproMessage from '../AmeliproMessage/AmeliproMessage.vue';
	import { AmeliproRadioGroupItem } from './types';

	const props = defineProps({
		ariaRequired: {
			type: Boolean,
			default: false,
		},
		defaultChecked: {
			type: String,
			default: undefined,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		error: {
			type: Boolean,
			default: false,
		},
		fullHorizontal: {
			type: Boolean,
			default: false,
		},
		groupId: {
			type: String,
			required: true,
		},
		groupLabel: {
			type: String,
			default: '',
		},
		horizontal: {
			type: Boolean,
			default: false,
		},
		horizontalLabel: {
			type: Boolean,
			default: false,
		},
		items: {
			type: Array as PropType<AmeliproRadioGroupItem[]>,
			required: true,
		},
		pills: {
			type: Boolean,
			default: false,
		},
		requiredErrorMessage: {
			type: String,
			default: 'Ce champ est obligatoire',
		},
	});

	const slots = useSlots();
	const slotSubItem = (index: number): boolean => {
		if (props.horizontal || props.fullHorizontal) {
			return false;
		}
		return Boolean(slots[`subItem-${index}`]);
	};

	const selectedValue = ref<string | null>(props.defaultChecked || null);

	const checkErrorCondition = computed(() => Boolean(props.ariaRequired && selectedValue.value === null && props.error));

	const isChecked = (value: string): boolean => selectedValue.value === value;

	const ariaExpandedValue = (item: AmeliproRadioGroupItem, index: number): 'true' | 'false' | undefined => {
		if (slotSubItem(index)) {
			return selectedValue.value === item.value ? 'true' : 'false';
		}
		return undefined;
	};

	const emit = defineEmits(['input']);
	const emitChangeEvent = (item: AmeliproRadioGroupItem): void => {
		selectedValue.value = item.value;
		emit('input', selectedValue.value);
	};

	// is usefull for the services developers to clear radios buttons with an external button
	const clear = (): void => {
		selectedValue.value = null;
	};
	defineExpose({ clear });
</script>
<style lang="scss" scoped>
.margin-label {
	@media #{$media-up-md} {
		margin-top: 3px;
	}
}

input {
	position: absolute;
	width: 0;
	height: 0;
	opacity: 0;

	& + span {
		position: relative;
		cursor: pointer;
	}

	&:focus {
		& + span {
			outline: 1px dotted $ap-grey-darken1;
		}
	}

	&:disabled {
		& + span {
			cursor: default;
		}
	}
}
.amelipro-radio-group.classic input {

	& + span {
		padding-top: 0.25rem;
		padding-bottom: 0.25rem;
		padding-left: 2rem;

		&::before {
			position: absolute;
			top: 0.0781rem;
			left: 0;
			width: 1.625rem;
			height: 1.625rem;
			border-radius: 50%;
			border: 3px solid $ap-blue-darken1;
			background-color: transparent;
			content: "";
		}
	}

	&:checked {
		& + span {
			&::after {
				position: absolute;
				top: 0.4781rem;
				left: 0.4rem;
				width: 0.825rem;
				height: 0.825rem;
				border-radius: 50%;
				background-color: $ap-blue-darken1;
				content: "";
			}
		}
	}

	&:disabled {
		& + span {
			&::before {
				opacity: 0.3;
			}
		}
	}
}

.amelipro-radio-group.pills {
	& label {
		&:not(:last-child) {
			@media #{$media-up-sm} {
				margin-right: 16px;
			}
		}
	}
	& input {
		& + span {
			font-size: $font-size-sm;
			font-weight: $filter-font-weight;
			padding: $filter-padding-y $filter-padding-x;
			border-radius: $filter-radius;
			border: 1px solid $ap-blue-darken1;
			background-color: $ap-white;
			color: $ap-blue-darken1;

			&.radio-group__item__label__span__error {
				background-color: $ap-red-lighten4;
				border-color: $ap-red;
			}
		}

		&:checked {
			& + span {
				background-color: $ap-blue-lighten3;

				&::after {
					position: absolute;
					top: 1.1rem;
					left: 1.1rem;
					width: 1rem;
					height: 0.5rem;
					border-bottom: 2px solid $ap-blue-darken1;
					border-left: 2px solid $ap-blue-darken1;
					transform: rotate(-50deg);
					content: "";
				}
			}
		}

		&:disabled {
			& + span {
				background-color: $ap-grey-lighten2;
				border-color: $ap-grey;
				&::after {
					border-bottom-color: $ap-grey-darken1;
					border-left-color: $ap-grey-darken1;
				}
			}
		}
	}
}

.radio-group__label {
	font-weight: $label-font-weight;
}
</style>
